import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import { getUserProfile, UserProfile } from "../../API/UserProfileApi/GetUserProfile";
import ReactGA from "react-ga4";
import { useLocalization } from "../Context/MultiLanguageContext";
import { ChevronDown, ChevronUp, Check, LogOut } from "lucide-react";
import gmap from "../../assets/images/GMap.jpeg";
import waze from "../../assets/images/Waze.jpeg";
import mapIndia from "../../assets/images/mapIndia.png";
import appleMap from "../../assets/images/appleMap.png";
import { DeleteUserAsync } from "../../API/UserProfileApi/DeleteUserAsync";
import editIcon from "../../assets/icons/editPen.svg";
import { Link } from "lucide-react";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { getApiAccessRequestStatus } from "../../API/UserProfileApi/GetApiAccessRequestStatus";
import { requestApiAccess } from "../../API/UserProfileApi/RequestApiAccess";

type MapOption = "google" | "waze" | "mapindia" | "apple";

interface ProfileProps {
  updateUserName: (newUserName: string) => void;
}

interface TokenPayload extends JwtPayload {
  RoleId?: number;
}

const Profile: React.FC<ProfileProps> = ({ updateUserName }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [countryCode, setCountryCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [profileData, setProfileData] = useState({
    userId: 0,
    userName: '',
    email: '',
    phoneNumber: ''
  });
  const [open, setOpen] = useState(false);
  const { t, setLanguage, language } = useLocalization();
  const [defaultMap, setDefaultMap] = useState<MapOption | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMapDropdownOpen, setIsMapDropdownOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [languageItem, setLanguageItem] = useState([
    { label: "English", value: "en" },
    { label: "हिंदी [Hindi]", value: "Hindi" },
    { label: "മലയാളം [Malayalam]", value: "ml" },
    { label: "தமிழ் [Tamil]", value: "Tamil" },
    { label: "ಕನ್ನಡ [Kannada]", value: "kannada" }
  ])
  const [shareMessage, setShareMessage] = useState("");
  const [canShowApiKeyRequest, setCanShowApiKeyRequest] = useState(false);
  const [canRequestApiKey, setCanRequestApiKey] = useState(false);
  const [isApiRequestLoading, setIsApiRequestLoading] = useState(false);
  const [apiRequestStatus, setApiRequestStatus] = useState<string>("");

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });
  }, []);
  useEffect(() => {
    const storedMap = localStorage.getItem("defaultMap") as "google" | "waze" | "mapindia" | "apple" | null;
    if (storedMap) {
      setDefaultMap(storedMap);
    } else {
      localStorage.setItem("defaultMap", "google");
      setDefaultMap("google");
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }

    try {
      const decodedToken = jwtDecode<TokenPayload>(token);
      const roleId = Number(decodedToken.RoleId);
      const isGuestUser = roleId === 3;
      setCanShowApiKeyRequest(!isGuestUser);

      if (!isGuestUser) {
        getApiAccessRequestStatus(token, t)
          .then((response) => {
            setApiRequestStatus(response.status);
            setCanRequestApiKey(
              response.status === "NotRequested" || response.status === "Rejected"
            );
          })
          .catch(() => {
            setApiRequestStatus("");
            setCanRequestApiKey(false);
          });
      }
    } catch (error) {
      setCanShowApiKeyRequest(false);
      setCanRequestApiKey(false);
    }
  }, [t]);

  const generateReferralLink = () => {
    const isPreprod = window.location.hostname.includes("preprod");

    const firstName = profileData.userName?.split(" ")[0] || "";

    return isPreprod
      ? `https://preprod.getmethere.in/?ref=${profileData.userId}&by=${encodeURIComponent(firstName)}`
      : `https://getmethere.in/?ref=${profileData.userId}&by=${encodeURIComponent(firstName)}`;
  };

  const handleCopyReferral = async () => {
    try {
      const link = generateReferralLink();

      await navigator.clipboard.writeText(link);

      setShareMessage("Share link copied");

      setTimeout(() => {
        setShareMessage("");
      }, 2000);

    } catch (error) {
      // toast.error(t("somethingWentWrong"));
    }
  };

  const handleRequestApiKey = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      toast.error(t("tokenNotFound"));
      return;
    }

    setIsApiRequestLoading(true);
    try {
      const response = await requestApiAccess(token, t);
      toast.success(response.message);
      setApiRequestStatus("Requested");
      setCanRequestApiKey(false);
    } catch (error) {
      // API helper already handles error toast
    } finally {
      setIsApiRequestLoading(false);
    }
  };
  const handleSetDefaultMap = (map: "google" | "waze" | "mapindia" | "apple") => {
    setDefaultMap(map);
    localStorage.setItem("defaultMap", map);
  };

  const handleUpdateClick = () => {
    ReactGA.event({
      category: "Profile",
      action: "Update Profile Clicked",
      label: profileData.userName,
    });
    navigate('/update-user-profile', {
      state: { userProfile: { userName: profileData.userName, email: profileData.email, phoneNumber: profileData.phoneNumber } }
    });
  };


  useEffect(() => {

    if (profileData.phoneNumber) {
      const phoneLength = profileData.phoneNumber.length;
      if (phoneLength > 10) {
        setCountryCode(profileData.phoneNumber.slice(0, phoneLength - 10));
        setPhoneNumber(profileData.phoneNumber.slice(-10));
      } else {
        setCountryCode("+");
        setPhoneNumber(profileData.phoneNumber);
      }
    }
  }, [profileData.phoneNumber]);


  useEffect(() => {
    if (location.state?.userName && location.state?.userId) {
      setProfileData({
        userId: location.state.userId,
        userName: location.state.userName,
        email: location.state.email,
        phoneNumber: location.state.phoneNumber,
      });
      ReactGA.event({
        category: "Profile",
        action: "Profile Loaded From State",
        label: location.state.userName,
      });
    } else {
      const fetchProfile = async () => {
        try {
          const data = await getUserProfile(t);
          setProfileData({
            userId: data.id,
            userName: data.userName,
            email: data.email,
            phoneNumber: data.phoneNumber,
          });

          ReactGA.event({
            category: "Profile",
            action: "Profile Fetched",
            label: data.userName,
          });

        } catch (error) {
          // toast.error(t("profileDataloaderror"));
        }
      };
      fetchProfile();
    }
  }, [location.state, navigate]);




  const fetchUserProfile = async () => {
    try {
      const data = await getUserProfile(t);
      setUserProfile(data);
      setProfileData({
        userId: data.id,
        userName: data.userName,
        email: data.email,
        phoneNumber: data.phoneNumber,
      });
      updateUserName(data.userName);

      ReactGA.event({
        category: "Profile",
        action: "User Profile Re-Fetched",
        label: data.userName,
      });
      navigate('/profile', {
        state: {
          userId: data.id,
          userName: data.userName,
          email: data.email,
          phoneNumber: data.phoneNumber,
        },
      });
    } catch (error) {
      // toast.error(t("userProfileError"));
    }
  };


  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('userName');

    sessionStorage.clear();

    ReactGA.event({
      category: "Profile",
      action: "Logout",
      label: profileData.userName,
    });
    navigate("/", { replace: true });
  };
  const handleDeleteAccountClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    const token = localStorage.getItem('token');

    if (!token) {
      toast.error(t("tokenNotFound"));
      // console.error("Token from localStorage:", localStorage.getItem('userToken'));
      return;
    }

    try {
      await DeleteUserAsync(token);
      localStorage.removeItem('token');
      setIsDeleteModalOpen(false);
      handleLogout(); // Navigate away after deletion
      toast.success(t("accountDeletedSuccess"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("deleteFailed"));
    }
  };



  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };
  const handleProfile = () => {
    navigate('/profile')
  }

  const mapOptions = [
    { value: "google", label: t("googleMap") || "Google Maps", shortLabel: t("google") || "Google", icon: gmap },
    { value: "waze", label: t("wazeMap") || "Waze Map", shortLabel: t("waze") || "Waze", icon: waze },
    { value: "mapindia", label: t("mapplsMap") || "Mappls Map", shortLabel: t("mappls") || "MapMyIndia", icon: mapIndia },
    {
      value: "apple",
      label: t("appleMaps") || "Apple Maps",
      shortLabel: t("apple") || "Apple",
      icon: appleMap
    },
  ];

  // Sort map options to show selected one first
  const sortedMapOptions = defaultMap
    ? [
      mapOptions.find(opt => opt.value === defaultMap)!,
      ...mapOptions.filter(opt => opt.value !== defaultMap)
    ]
    : mapOptions;

  const selectedMapOption = mapOptions.find(opt => opt.value === defaultMap);

  return (
    <div className="flex flex-col min-h-screen bg-gray-100 overflow-hidden">
      <Header userName={profileData.userName} fetchUserProfile={fetchUserProfile} handleLogout={handleLogout} />

      {/* Main Content Container */}
      <div className="p-4 md:p-6 flex-grow">

        {/* Page Title */}
        <h1 className="text-xl md:text-2xl lg:text-3xl font-bold mb-4 md:mb-6 text-gray-800">
          {t("myProfile")}
        </h1>

        {/* Profile Card */}
        <div className="w-full max-w-7xl mx-auto p-4 md:p-6 bg-white rounded-lg shadow-md">

          {(!profileData.userName || !profileData.phoneNumber) && (
            <div className="mb-6 px-4 py-2 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-between">

              <p className="text-sm text-gray-800">
                <span className="font-medium">
                  {t("completeProfile") || "Complete your profile."}
                </span>
              </p>

              <button
                onClick={handleUpdateClick}
                className="p-1 hover:bg-yellow-100 rounded-md"
              >
                <img
                  src={editIcon}
                  alt="edit"
                  className="w-4 h-4 opacity-70 hover:opacity-100"
                />
              </button>

            </div>
          )}

          {/* Form Grid */}
          <form className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">

            {/* Full Name Field */}
            <div className="lg:col-span-1">
              <label htmlFor="username" className="block text-sm md:text-base font-medium text-gray-700 mb-2">
                {t("FullName")}
              </label>
              <input
                id="username"
                type="text"
                value={profileData.userName}
                className="w-full px-3 py-2 lg:px-3 lg:py-2 border border-gray-200 rounded-md bg-gray-50 text-black text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                readOnly
              />
            </div>

            {/* Email Field */}
            <div className="lg:col-span-1">
              <label htmlFor="email" className="block text-sm md:text-base font-medium text-gray-700 mb-2">
                {t("emailId")}
              </label>
              <input
                id="email"
                type="email"
                value={profileData.email}
                className="w-full px-3 py-2 lg:px-3 lg:py-2 border border-gray-200 rounded-md bg-gray-50 text-black text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                readOnly
              />
            </div>

            {/* Phone Number Field */}
            <div className="sm:col-span-2 lg:col-span-1">
              <label htmlFor="phone" className="block text-sm md:text-base font-medium text-gray-700 mb-2">
                {t("phoneNumber")}
              </label>
              <input
                id="phone"
                type="text"
                value={profileData.phoneNumber}
                disabled={profileData.email === 'guest@getmethere.in'}
                className="w-full px-3 py-2 lg:px-3 lg:py-2 border border-gray-200 rounded-md bg-gray-50 text-black text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                readOnly
              />
            </div>

            {/* Language Selector */}
            <div className="relative sm:col-span-2 lg:col-span-1">
              <div className="block text-sm md:text-base font-medium text-gray-700 mb-2">
                {t("selectLanguage")}
              </div>

              <label
                htmlFor="language"
                onClick={() => setOpen(!open)}
                className="w-full px-3 py-2 lg:px-3 lg:py-2 border border-gray-200 rounded-md bg-gray-50 text-black text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer flex justify-between items-center transition-all duration-200 hover:bg-gray-100"
              >
                {t(language)}
                {open ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </label>

              {open && (
                <div className="absolute z-20 mt-1 w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 max-h-48 overflow-y-auto">
                  <ul className="py-1 text-sm md:text-base text-gray-700">
                    {languageItem.map((item) => (
                      <li
                        key={item.value}
                        onClick={() => { setLanguage(item.value); setOpen(false); }}
                        className={`px-3 py-2 hover:bg-blue-100 rounded-md cursor-pointer flex justify-between w-full transition-colors duration-150 ${language === item.value ? "border border-blue-600 text-blue-600 bg-blue-50 font-medium" : ""}`}
                      >
                        <span className="truncate">{item.label}</span>
                        {language === item.value && (
                          <span>
                            <Check size={20} />
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Default Map Selection Dropdown */}
            <div className="relative sm:col-span-2 lg:col-span-1">
              <label className="block text-sm md:text-base font-medium text-gray-700 mb-2">
                {t("defaultMap")}
              </label>

              <button
                type="button"
                onClick={() => setIsMapDropdownOpen(!isMapDropdownOpen)}
                className="w-full px-3 py-2 lg:px-3 lg:py-2 border border-gray-200 rounded-md bg-gray-50 text-black text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer flex justify-between items-center transition-all duration-200 hover:bg-gray-100"
              >
                <div className="flex items-center">
                  {selectedMapOption && (
                    <>
                      {typeof selectedMapOption.icon === 'string' ? (
                        <img src={selectedMapOption.icon} alt={selectedMapOption.label} className="mr-2 h-5 w-5 rounded" />
                      ) : (
                        <span className="mr-2">{selectedMapOption.icon}</span>
                      )}
                      <span className="hidden sm:inline">{selectedMapOption.label}</span>
                      <span className="sm:hidden">{selectedMapOption.shortLabel}</span>
                    </>
                  )}
                </div>
                {isMapDropdownOpen ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </button>

              {isMapDropdownOpen && (
                <div className="absolute z-20 mt-1 w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                  <ul className="py-1 text-sm md:text-base text-gray-700">
                    {sortedMapOptions.map((option) => (
                      <li
                        key={option.value}
                        onClick={() => {
                          handleSetDefaultMap(option.value as MapOption);
                          setIsMapDropdownOpen(false);
                        }}
                        className={`px-3 py-2 hover:bg-blue-100 cursor-pointer flex justify-between items-center transition-colors duration-150 ${defaultMap === option.value ? "border rounded-md  border-blue-600 text-blue-600 bg-blue-50 font-medium" : ""
                          }`}
                      >
                        <div className="flex items-center">
                          {typeof option.icon === 'string' ? (
                            <img src={option.icon} alt={option.label} className="mr-2 h-5 w-5 rounded" />
                          ) : (
                            <span className="mr-2">{option.icon}</span>
                          )}
                          <span className="hidden sm:inline">{option.label}</span>
                          <span className="sm:hidden">{option.shortLabel}</span>
                        </div>
                        {defaultMap === option.value && (
                          <span>
                            <Check size={20} />
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="sm:col-span-2 lg:col-span-2">
              <label className="block text-sm md:text-base font-medium text-transparent mb-2">
                .
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">

                <button
                  type="button"
                  onClick={() => navigate(`/referral-user/${profileData.userId}`)}
                  className="w-full sm:w-auto min-w-0 sm:min-w-[180px] h-[44px] px-4 border border-blue-200 rounded-md bg-gray-50 text-blue-600 text-sm md:text-base text-center hover:bg-gray-100 hover:text-blue-700 transition-all duration-200"
                >
                  {t("referralHistory") || "Referral History"}
                </button>

                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={handleCopyReferral}
                    className="w-full sm:w-auto min-w-0 sm:min-w-[160px] h-[44px] px-4 border border-blue-200 bg-gray-50 rounded-md hover:bg-gray-100 transition flex items-center justify-center gap-2"
                  >
                    <Link size={18} className="text-blue-600 shrink-0" />

                    <span className="text-sm md:text-base text-blue-600 whitespace-nowrap">
                      {t("shareLink") || "Share Link"}
                    </span>
                  </button>

                  {shareMessage && (
                    <p className="text-xs text-green-600 mt-1 text-center whitespace-nowrap">
                      {shareMessage}
                    </p>
                  )}
                </div>



                {canShowApiKeyRequest && profileData.email !== "guest@getmethere.in" && (
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={handleRequestApiKey}
                      disabled={!canRequestApiKey || isApiRequestLoading}
                      className={`w-full sm:w-auto min-w-0 sm:min-w-[180px] h-[44px] px-4 border rounded-md text-sm md:text-base text-center transition-all duration-200 ${!canRequestApiKey || isApiRequestLoading
                        ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "border-blue-200 bg-gray-50 text-blue-600 hover:bg-gray-100 hover:text-blue-700"
                        }`}
                    >
                      {isApiRequestLoading
                        ? t("requesting") || "Requesting..."
                        : apiRequestStatus === "Approved"
                          ? t("approved") || "Approved"
                          : canRequestApiKey
                            ? t("requestApiKey") || "Request API Key"
                            : t("apiKeyOnRequest") || "API Key On Request"}
                    </button>
                    {/* {apiRequestStatus === "Approved" && (
                      <p className="text-xs text-green-600 mt-1 text-center sm:text-left">
                        Check mail for API key
                      </p>
                    )} */}
                  </div>
                )}
              </div>
            </div>

            {profileData.email !== 'guest@getmethere.in' && (
              <div className="sm:col-span-2 lg:col-span-4">
                <div className="mt-4 flex flex-col gap-2">

                  {/* Delete Account */}
                  <button
                    type="button"
                    onClick={handleDeleteAccountClick}
                    className="text-sm md:text-base underline cursor-pointer hover:text-red-700 transition-colors duration-200 inline-block w-fit text-left"
                  >
                    {t("deleteAccount")}
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="sm:col-span-2 lg:col-span-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 mt-6">

              {profileData.email !== 'guest@getmethere.in' && (
                <button
                  onClick={handleUpdateClick}
                  className="py-2 px-6 lg:py-2 lg:px-6 bg-blue-600 text-white text-sm md:text-base rounded-md shadow-md hover:bg-blue-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 font-medium"
                >
                  {t("editProfile")}
                </button>
              )}
              <button
                onClick={() => navigate('/home')}
                className="py-2 px-6 lg:py-2 lg:px-6 bg-gray-300 text-gray-800 text-sm md:text-base rounded-md shadow-md hover:bg-gray-400 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-medium"
              >
                {t("cancel")}
              </button>

              <button
                onClick={handleLogout}
                className="py-2 px-6 lg:py-2 lg:px-6 bg-gray-300 text-gray-800 text-sm md:text-base rounded-md shadow-md hover:bg-gray-400 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-medium"
              >
                {t("logout")}
              </button>
            </div>
          </form>
        </div>
      </div>

      <Footer />

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-[1000] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 mx-4">
            <h2 className="text-xl md:text-2xl font-bold mb-4 text-gray-800">
              {t("deleteAccountConfirm")}
            </h2>
            <p className="mb-6 text-sm md:text-base text-gray-600 leading-relaxed">
              {t("continueAccountDeletion")}
            </p>
            <div className="flex flex-col sm:flex-row justify-between gap-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 bg-gray-300 text-gray-800 text-sm md:text-base rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-medium"
              >
                {t("cancel")}
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white text-sm md:text-base rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 font-medium"
              >
                {t("continue")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default Profile;
